import { api } from './api';
import { getAccessToken } from '../utils/authToken';

export interface BulkInviteResponse {
  task_id: string;
  total_emails: number;
  total_batches: number;
  message: string;
}

export interface BulkInviteStatusResponse {
  task_id: string;
  status: string;
  progress: number;
  successful: number;
  failed: number;
  skipped: number;
  credits_granted?: number;
  total: number;
  errors: Array<{ email?: string; error: string }>;
  skipped_details?: Array<{ email: string; reason: string }>;
  batches_completed: number;
  total_batches: number;
}

export interface ClientSummary {
  id: number;
  email: string;
  full_name?: string | null;
  is_registered: boolean;
  credits_remaining: number;
  exercises_used: number;
  last_invited_at?: string | null;
  last_invite_email_status?: string | null;
  invitation_expires_at?: string | null;
  created_at: string;
}

export interface InsurerAnalytics {
  org_id: number;
  org_name: string;
  clients_total: number;
  clients_registered: number;
  pending_invites: number;
  credits_granted_mtd: number;
  outstanding_ledger_cents: number;
  exercises_completed: number;
  unused_credits: number;
  period_start: string;
}

export interface ClientListResponse {
  clients: ClientSummary[];
  total: number;
}

export interface PartnerLedgerEntry {
  id: number;
  client_email: string;
  client_name?: string | null;
  amount_cents: number;
  status: string;
  entry_type?: string | null;
  exercise_id?: number | null;
  invoice_reference?: string | null;
  created_at: string;
  purchase_id: number;
  partner_token_tier?: 'simple' | 'gold' | 'premium' | null;
}

export interface PartnerLedgerListResponse {
  entries: PartnerLedgerEntry[];
  total_amount_cents: number;
  total: number;
  totals?: {
    total_entries: number;
    outstanding_amount_cents: number;
    pending_amount_cents?: number;
    invoiced_amount_cents: number;
    paid_amount_cents: number;
    void_amount_cents: number;
  };
}

export type PartnerTokenTier = 'simple' | 'gold' | 'premium';

export interface GrantCreditResponse {
  purchase_id: number;
  ledger_id: number;
  credits_remaining: number;
  token_tier: PartnerTokenTier;
  amount_cents: number;
  message: string;
}

export interface TeamMember {
  id: number;
  email: string;
  full_name?: string | null;
  org_role?: string | null;
  is_active: boolean;
  is_verified: boolean;
  created_at: string;
  invitation_expires_at?: string | null;
  last_invited_at?: string | null;
  last_invite_email_status?: string | null;
}

export interface ClientPurchaseSummary {
  id: number;
  credits_remaining: number;
  credits_total: number;
  amount_cents: number;
  partner_token_tier?: PartnerTokenTier | null;
  status: string;
  created_at: string;
}

export interface ClientExerciseSummary {
  id: number;
  exercise_name: string;
  status: string;
  created_at: string;
}

export interface ClientDetail {
  client: ClientSummary;
  purchases: ClientPurchaseSummary[];
  exercises: ClientExerciseSummary[];
  ledger_entries: PartnerLedgerEntry[];
}

export interface InvitePreview {
  subject: string;
  body_preview: string;
  reply_to?: string | null;
}

export interface InsurerOrgInfo {
  id: number;
  slug: string;
  name: string;
  status: string;
  billing_email?: string | null;
  invite_contact_email?: string | null;
  has_logo?: boolean;
  mfa_policy?: 'optional' | 'mandatory';
  created_at: string;
  approved_at?: string | null;
}

class InsurerService {
  private baseUrl = '/v1/insurer';

  async getOrg(): Promise<InsurerOrgInfo> {
    const response = await api.get(`${this.baseUrl}/org`);
    return response.data;
  }

  async bulkInviteClients(
    file: File,
    grantCredit = true,
    tokenTier: PartnerTokenTier = 'simple'
  ): Promise<BulkInviteResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('grant_credit', grantCredit ? 'true' : 'false');
    formData.append('token_tier', tokenTier);

    const token = getAccessToken();
    const response = await fetch(`${process.env.REACT_APP_API_URL || ''}${this.baseUrl}/bulk-invite`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Failed to upload file' }));
      throw new Error(error.detail || 'Failed to upload file');
    }

    return response.json();
  }

  async inviteClientsByEmail(
    emails: string[],
    grantCredit = true,
    tokenTier: PartnerTokenTier = 'simple'
  ): Promise<BulkInviteResponse> {
    const response = await api.post(`${this.baseUrl}/invite-emails`, {
      emails,
      grant_credit: grantCredit,
      token_tier: tokenTier,
    });
    return response.data;
  }

  async inviteClientsWithTiers(
    invites: Array<{ email: string; token_tier: PartnerTokenTier }>,
    grantCredit = true
  ): Promise<BulkInviteResponse> {
    const response = await api.post(`${this.baseUrl}/invite-emails`, {
      invites,
      grant_credit: grantCredit,
    });
    return response.data;
  }

  async getBulkInviteStatus(taskId: string): Promise<BulkInviteStatusResponse> {
    const response = await api.get(`${this.baseUrl}/bulk-invite/status/${taskId}`);
    return response.data;
  }

  async listClients(): Promise<ClientListResponse> {
    const response = await api.get(`${this.baseUrl}/clients`);
    return response.data;
  }

  async getClientDetail(userId: number): Promise<ClientDetail> {
    const response = await api.get(`${this.baseUrl}/clients/${userId}`);
    return response.data;
  }

  async getInvitePreview(kind: 'client' | 'team' = 'client'): Promise<InvitePreview> {
    const response = await api.get(`${this.baseUrl}/invite-preview`, { params: { kind } });
    return response.data;
  }

  async grantClientCredit(
    userId: number,
    tokenTier: PartnerTokenTier = 'simple'
  ): Promise<GrantCreditResponse> {
    const response = await api.post(`${this.baseUrl}/clients/${userId}/grant-credit`, {
      token_tier: tokenTier,
    });
    return response.data;
  }

  async listPartnerLedger(fromDate?: string, toDate?: string, limit?: number): Promise<PartnerLedgerListResponse> {
    const params = new URLSearchParams();
    if (fromDate) params.append('from_date', fromDate);
    if (toDate) params.append('to_date', toDate);
    if (limit !== undefined) params.append('limit', limit.toString());
    const query = params.toString() ? `?${params.toString()}` : '';
    const response = await api.get(`${this.baseUrl}/partner-ledger${query}`);
    return response.data;
  }

  async listTeamMembers(): Promise<TeamMember[]> {
    const response = await api.get(`${this.baseUrl}/team`);
    return response.data;
  }

  async inviteTeamMember(email: string, fullName?: string): Promise<{ message: string }> {
    const response = await api.post(`${this.baseUrl}/team/invite`, {
      email,
      full_name: fullName || undefined,
    });
    return response.data;
  }

  async removeTeamMember(userId: number): Promise<{ message: string }> {
    const response = await api.delete(`${this.baseUrl}/team/${userId}`);
    return response.data;
  }

  async updateOrg(data: {
    name?: string;
    billing_email?: string;
    invite_contact_email?: string;
    mfa_policy?: 'optional' | 'mandatory';
  }): Promise<InsurerOrgInfo> {
    const response = await api.patch(`${this.baseUrl}/org`, data);
    return response.data;
  }

  async uploadOrgLogo(file: File): Promise<InsurerOrgInfo> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post(`${this.baseUrl}/org/logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  async getOrgLogoObjectUrl(): Promise<string | null> {
    try {
      const response = await api.get(`${this.baseUrl}/org/logo`, { responseType: 'blob' });
      return URL.createObjectURL(response.data);
    } catch {
      return null;
    }
  }

  async resendClientInvite(userId: number): Promise<{ message: string }> {
    const response = await api.post(`${this.baseUrl}/clients/${userId}/resend-invite`);
    return response.data;
  }

  async resendTeamInvite(userId: number): Promise<{ message: string }> {
    const response = await api.post(`${this.baseUrl}/team/${userId}/resend-invite`);
    return response.data;
  }

  async getAnalytics(): Promise<InsurerAnalytics> {
    const response = await api.get(`${this.baseUrl}/analytics`);
    return response.data;
  }

  async revokeClientCredit(userId: number): Promise<{ message: string }> {
    const response = await api.post(`${this.baseUrl}/clients/${userId}/revoke-credit`);
    return response.data;
  }

  async unlinkClient(userId: number): Promise<{ message: string }> {
    const response = await api.delete(`${this.baseUrl}/clients/${userId}`);
    return response.data;
  }

  async updateTeamMemberRole(userId: number, orgRole: 'org_admin' | 'org_member'): Promise<{ message: string }> {
    const response = await api.patch(`${this.baseUrl}/team/${userId}`, { org_role: orgRole });
    return response.data;
  }

  async transferOrgAdmin(newAdminUserId: number): Promise<{ message: string }> {
    const response = await api.post(`${this.baseUrl}/team/transfer-admin`, {
      new_admin_user_id: newAdminUserId,
    });
    return response.data;
  }

  async sendTestEmail(to: string): Promise<{ ok: boolean; detail?: string }> {
    const response = await api.post(`${this.baseUrl}/email/test`, { to });
    return response.data;
  }

  downloadClientsCsv(): void {
    this.downloadCsv(`${this.baseUrl}/clients/export`, 'clients.csv');
  }

  downloadLedgerCsv(fromDate?: string, toDate?: string): void {
    const params = new URLSearchParams();
    if (fromDate) params.append('from_date', fromDate);
    if (toDate) params.append('to_date', toDate);
    const query = params.toString() ? `?${params.toString()}` : '';
    this.downloadCsv(`${this.baseUrl}/partner-ledger/export${query}`, 'partner-ledger.csv');
  }

  private downloadCsv(url: string, filename: string): void {
    const token = getAccessToken();
    fetch(`${process.env.REACT_APP_API_URL || ''}${url}`, {
      credentials: 'include',
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.blob())
      .then((blob) => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        link.click();
        URL.revokeObjectURL(link.href);
      });
  }
}

export const insurerService = new InsurerService();
